"use strict"

import React, { useEffect, useState } from "react"
import { Modal, Table, Dropdown, DropdownButton } from "react-bootstrap"

import DataExportButton from "./DataExportButton"
import { arrayWithContents, isNumber } from "../../utils"
import {
  isLTPM,
  getDate,
  TimezoneDetails,
  withApplicationConfig,
} from "../../../components/shared_components_registration"
import ToggleActionLayers from "../plotting/GraphComponents/ToggleActionLayers"
import { FeatureTriangle } from "../plotting/GraphComponents/ActionLayer/FeatureTriangles"
import { fillColors } from "../plotting/GraphComponents/ActionLayer"

const registration = {
  data_modal_title: (intl) =>
    isLTPM ? intl.formatMessage({ id: "graphing.download.graph.data" }) : "Download Graph Data",
  comment_modal_title: (intl) =>
    isLTPM ? intl.formatMessage({ id: "graphing.download.comments.data" }) : "Download Comment Data",
  modal_name: () => (isLTPM ? "common-modal" : "definitions-modal"),
  download_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.download" }) : "Download Data"),
  action_filter_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.filter" }) : "Filter Actions"),
  number_of_rows: (intl) =>
    isLTPM ? intl.formatMessage({ id: "graphing.number.of.rows" }) : "Number of rows",
  all_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.all" }) : "All"),
  day_label: (intl) => (isLTPM ? intl.formatMessage({ id: "graphing.timelabel.day" }) : "Day"),
  time_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.time" }) : "Time"),
  action_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.action" }) : "Action"), // Called dynamically
  comment_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.comment" }) : "Comment"), // Called dynamically
  operator_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.operator" }) : "Operator"), // Called dynamically
  actor_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.actor" }) : "Actor"), // Called dynamically
  priority_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.priority" }) : "Priority"), // Called dynamically
  name_label: (intl) => (isLTPM ? intl.formatMessage({ id: "graphing.run.identifier" }) : "Run Identifier"), // Called dynamically
  reactor_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.reactor" }) : "Reactor"), // Called dynamically
  no_data_msg: (intl) => (isLTPM ? intl.formatMessage({ id: "graphing.no.data" }) : "No data available"),
}

export function DataModal(props) {
  return <TableModalBase {...props} modal_title={registration.data_modal_title(props.intl)} />
}

export function CommentsModal(props) {
  return (
    <TableModalBase
      {...props}
      modal_title={registration.comment_modal_title(props.intl)}
      show_action_toggle={true}
    />
  )
}

const _TableModalBase = (props) => {
  const [rowCount, setRowCount] = useState(10)
  const [tz, setTZ] = useState(null)

  useEffect(() => {
    setTZ(props.selected_timezone)
  }, [props.selected_timezone])

  const options = [10, 25, 50, 100, 250, 500, 750, 1000, "Show All"]

  const getDataRows = (e, sel) => {
    e.preventDefault()
    if (sel !== rowCount) {
      setRowCount(sel)
    }
  }

  const { table_data, filename, exportDataThunk, show_action_toggle, config } = props
  const [allowDataDownload, setAllowDataDownload] = useState(true)
  useEffect(() => {
    if (isLTPM && config?.user?.permissions) {
      // Handle use case on LTPM where certain user types shouldn't be able to download data from these modals
      setAllowDataDownload(
        config.user.permissions.includes(
          show_action_toggle ? "can_download_run_comments" : "can_download_run_obs_data",
        ),
      )
    }
  }, [config])

  const excludedColumns = [
    "seq_id",
    "position",
    "position_start",
    "position_end",
    "frequency",
    "mutation_type",
    "snp_type",
    "mutation_category",
    "ref_seq",
    "new_seq",
    "gene_name",
    "gene_position",
    "gene_product",
    "Sample ID(s)",
    "Method(s)",
    "Sample Type",
    "Sample ID",
    "Run ID",
    "Reactor",
  ]
  let excludedIndexes = []

  const headers = table_data ? table_data[0] : []
  let timestamp_col = null
  let triangle_col = null
  let rows = table_data ? table_data.slice(1).reverse() : []
  if (rowCount !== "Show All") {
    rows = rows.slice(0, rowCount + 1)
  }
  return (
    <Modal
      show={props.show}
      onHide={props.onHide}
      backdrop={"static"}
      size={"lg"}
      dialogClassName={registration.modal_name()}
    >
      <Modal.Header closeButton>
        <Modal.Title>{props.modal_title}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxWidth: "95vw" }}>
        {tz && (
          <div style={{ marginBottom: "10px" }}>
            <TimezoneDetails />
          </div>
        )}
        <div className={"row"} style={{ margin: "0", marginBottom: "10px" }}>
          <div className={"col-4"} style={{ padding: "0" }}>
            <div style={{ display: "flex", marginRight: "auto", alignItems: "center" }}>
              <span style={{ marginRight: "5px" }}>{registration.download_label(props.intl)}:</span>
              <DataExportButton
                intl={props.intl}
                filename={filename}
                disabled={!arrayWithContents(rows) || !allowDataDownload}
                onHide={props.onHide}
                dataThunk={exportDataThunk}
                small
              />
            </div>
          </div>
          <div className={"col-4"} style={{ padding: "0" }}>
            {show_action_toggle && (
              <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{ marginRight: "5px" }}>{registration.action_filter_label(props.intl)}:</span>
                <ToggleActionLayers download={false} height={0} extraStyle={{}} />
              </div>
            )}
          </div>
          <div className={"col-4"} style={{ padding: "0" }}>
            <div style={{ display: "flex", float: "right", alignItems: "center" }}>
              <span style={{ marginRight: "5px" }}>{registration.number_of_rows(props.intl)}:</span>
              <DropdownButton
                variant={"outline-primary"}
                title={!isNumber(rowCount) ? "All" : rowCount}
                id={"number_rows_selector"}
                disabled={!arrayWithContents(rows)}
                size={"sm"}
              >
                {options
                  .filter((sel) => !(isNumber(sel) && sel > table_data.length))
                  .map((sel) => (
                    <Dropdown.Item key={sel} name={sel} onClick={(e) => getDataRows(e, sel)}>
                      {!isNumber(sel) ? <p>{registration.all_label(props.intl)}</p> : <p>{sel}</p>}
                    </Dropdown.Item>
                  ))}
              </DropdownButton>
            </div>
          </div>
        </div>
        {arrayWithContents(table_data) ? (
          <div style={{ maxHeight: "calc(90vh - 200px)", overflow: "auto", padding: "0 10px" }}>
            <Table className={"fixedHeadTable table table-hover table-responsive"}>
              <thead>
                {arrayWithContents(headers) && (
                  <tr>
                    {headers.map((header, idx) => {
                      let label = header
                      if (["day"].includes(header.toLowerCase())) {
                        label = registration.day_label(props.intl)
                      } else if (["time", "timestamp"].includes(header.toLowerCase())) {
                        timestamp_col = idx
                        label = registration.time_label(props.intl)
                      } else if (
                        ["action", "comment", "operator", "actor", "priority", "name", "reactor"].includes(
                          header.toLowerCase(),
                        )
                      ) {
                        excludedIndexes.push(idx)
                        label = registration[`${header.toLowerCase()}_label`](props.intl)
                      } else if (excludedColumns.includes(header)) {
                        excludedIndexes.push(idx)
                      } else if (label.toLowerCase() === "action_type") {
                        label = ""
                        triangle_col = idx
                      }

                      const label_parts = label.split(" [").map((part) => part.replace("]", ""))
                      return (
                        <th
                          className={"text-center"}
                          style={{
                            maxWidth: "400px",
                            minWidth: triangle_col === idx ? "35px" : "100px",
                            wordWrap: "break-word",
                            fontSize: "90%",
                          }}
                          key={`${header}_${idx}`}
                        >
                          {label_parts.map((label, i) => (
                            <div key={`${label}_${i}`}>{i === 0 ? label : `[${label}]`}</div>
                          ))}
                        </th>
                      )
                    })}
                  </tr>
                )}
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={`row_${idx}`}>
                    {row.map((data, i) => {
                      if (i === timestamp_col) {
                        data = getDate(data) || "-"
                      } else if (i === triangle_col) {
                        data = (
                          <div style={{ height: "15px", width: "15px" }}>
                            <svg style={{ height: "15px", width: "15px" }}>
                              <FeatureTriangle xpos={5} ft={data} getFillColor={(val) => fillColors[val]} />
                            </svg>
                          </div>
                        )
                      } else if (isNumber(data) && !excludedIndexes.includes(i)) {
                        data = data.toFixed(3)
                      } else if (!data || data === "") {
                        data = "-"
                      }
                      return (
                        <td
                          key={`row${idx}-col${i}`}
                          style={{
                            maxWidth: "400px",
                            minWidth: triangle_col === i ? "35px" : "100px",
                            wordWrap: "break-word",
                            fontSize: "90%",
                          }}
                          className={"text-center"}
                        >
                          {data}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div>
            <hr />
            {registration.no_data_msg(props.intl)}
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}

const TableModalBase = withApplicationConfig(_TableModalBase)
